import React from 'react';
import PropTypes from 'prop-types';
import CreditCard from '@mui/icons-material/CreditCard';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Checkbox, FormHelperText } from '@mui/material';
import { Button, TextField, IconButton, Autocomplete } from '@totalsoft/rocket-ui';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import CancelIcon from '@mui/icons-material/Cancel';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import OfferPartnerBankAccountSummary from './OfferPartnerBankAccountSummary';
import { withRights } from 'providers/RightsConsumer';
import { Prompt } from 'react-router-dom';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { GridActionsWrapper } from 'components/common/CommonStyles';

const OfferPartnerBankAccountComponent = ({
  bankAccounts,
  loadBanks,
  currencies,
  bankAccountTypes,
  t,
  onPropertyChange,
  onExpand,
  addBankAccount,
  saveBankAccounts,
  partnerId,
  localBankAccounts,
  deleteBankAccount,
  cardExpanded,
  saveDisabled,
  validationBankSelection,
  clientBankaccountWrite,
  validDefaultBankAccounts,
  rights,
  saving
}) => {
  const onBankAccountBlur = () => {
    if (bankAccounts) {
      bankAccounts.forEach(item => {
        if (item.bankAccount?.indexOf(' ') >= 0) item.bankAccount = item.bankAccount.replace(/\s/g, '');
      });
    }
  };

  const disabled = rights.readOnlyFull || !clientBankaccountWrite;

  return (
    <CollapseOrBlankCard
      id={'offerPartnerBankAccountCard'}
      icon={CreditCard}
      title={t('OfferEdit.Client.BankAccount.Title')}
      expanded={cardExpanded}
      subheader={<OfferPartnerBankAccountSummary bankAccounts={bankAccounts} />}
      hideSubheaderOnExpand
      onToggle={onExpand}
      canExpand={partnerId != null}
      content={
        <Grid container>
          <Prompt when={!saveDisabled} message={t('LeavingWithoutSaving')} />
          <Grid item xs={12} sm={12} md={12}>
            <CardNoResultsWrapper content={bankAccounts}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>{t('OfferEdit.Client.BankAccount.Name')}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{t('OfferEdit.Client.BankAccount.Account')}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{t('OfferEdit.Client.BankAccount.AccountType')}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{t('OfferEdit.Client.BankAccount.Currency')}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{t('OfferEdit.Client.BankAccount.Default')}</strong>
                    </TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bankAccounts.map((item, idx) => {
                    const accountUsedInERP = item.chBankAccountId !== null && item.chBankAccountId !== undefined;

                    const bankValue = !item.bankId ? null : { bankId: item.bankId, bankName: item.bankName };

                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <Autocomplete
                            fullWidth
                            isSearchable
                            value={bankValue}
                            valueKey='bankId'
                            labelKey='bankName'
                            onChange={onPropertyChange(idx)('bankId', 'bankName')}
                            isClearable={false}
                            loadOptions={loadBanks}
                            error={!isValid(validationBankSelection[idx].bankId)}
                            helperText={getErrors(validationBankSelection[idx].bankId)}
                            disabled={disabled}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            id={'offerPartnerBankAccountCard_' + idx + '_bankAccount'}
                            fullWidth
                            value={item.bankAccount || ''}
                            name='bankAccount'
                            onChange={onPropertyChange(idx)('bankAccount')}
                            onBlur={onBankAccountBlur}
                            error={!isValid(validationBankSelection[idx].bankAccount)}
                            helperText={getErrors(validationBankSelection[idx].bankAccount)}
                            disabled={disabled}
                          />
                        </TableCell>

                        <TableCell style={{ width: '20%' }}>
                          <Autocomplete
                            id={'offerPartnerBankAccountCard_' + idx + '_bankAccountType'}
                            fullWidth
                            value={item.bankAccountType}
                            valueKey='id'
                            labelKey='name'
                            options={bankAccountTypes}
                            onChange={onPropertyChange(idx)('bankAccountType')}
                            error={!isValid(validationBankSelection[idx].bankAccountType)}
                            helperText={getErrors(validationBankSelection[idx].bankAccountType)}
                            disabled={disabled}
                          />
                        </TableCell>

                        <TableCell style={{ width: '20%' }}>
                          <Autocomplete
                            id={'offerPartnerBankAccountCard_' + idx + '_currency'}
                            fullWidth
                            value={item.currencyId ? item.currencyId || '' : ''}
                            valueKey='currencyId'
                            labelKey='code'
                            simpleValue={true}
                            options={currencies}
                            onChange={onPropertyChange(idx)('currencyId')}
                            error={!isValid(validationBankSelection[idx].currencyId)}
                            helperText={getErrors(validationBankSelection[idx].currencyId)}
                            disabled={disabled}
                          />
                        </TableCell>

                        <TableCell>
                          <Checkbox
                            id={'offerPartnerBankAccountCard_' + idx + 'defaultAccount'}
                            checked={item.defaultAccount || false}
                            onChange={onCheckBoxChange(onPropertyChange(idx)('defaultAccount'))}
                            disabled={disabled}
                          />
                        </TableCell>

                        <TableCell>
                          <IconButton
                            id={'offerPartnerBankAccountCard_deleteBtn'}
                            color='primary'
                            variant='text'
                            size='small'
                            tooltip={accountUsedInERP ? t('OfferEdit.Client.BankAccount.CannotDeleteBankAccountUsedInERP') : t('Delete')}
                            disabled={disabled || accountUsedInERP}
                            onClick={() => deleteBankAccount(idx)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                {!validDefaultBankAccounts && (
                  <FormHelperText error={true} id='name-error-text'>
                    {t('OfferEdit.Client.BankAccount.ValidateUniqueDefaultBankAccounts')}
                  </FormHelperText>
                )}
              </Table>
            </CardNoResultsWrapper>
            <GridActionsWrapper mt={2}>
              <Button id={'offerPartnerBankAccountCard_addBtn'} onClick={() => addBankAccount()} disabled={disabled}>
                {t('Add')}
              </Button>
              <Button
                id={'offerPartnerBankAccountCard_saveBtn'}
                onClick={() => saveBankAccounts(partnerId, localBankAccounts)}
                disabled={saveDisabled || !isValid(validationBankSelection) || disabled || saving}
              >
                {saving ? t('Saving') : t('Save')}
              </Button>
            </GridActionsWrapper>
          </Grid>
        </Grid>
      }
    />
  );
};

OfferPartnerBankAccountComponent.propTypes = {
  bankAccounts: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  currencies: PropTypes.array.isRequired,
  bankAccountTypes: PropTypes.array.isRequired,
  loadBanks: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onCheckBoxChange: PropTypes.func,
  onExpand: PropTypes.func.isRequired,
  addBankAccount: PropTypes.func.isRequired,
  saveBankAccounts: PropTypes.func.isRequired,
  partnerId: PropTypes.number,
  localBankAccounts: PropTypes.array,
  deleteBankAccount: PropTypes.func.isRequired,
  cardExpanded: PropTypes.bool,
  saveDisabled: PropTypes.bool,
  validationBankSelection: PropTypes.object,
  clientBankaccountWrite: PropTypes.bool.isRequired,
  rights: PropTypes.object.isRequired,
  validDefaultBankAccounts: PropTypes.bool,
  saving: PropTypes.bool.isRequired
};

export default compose(withTranslation())(withRights(OfferPartnerBankAccountComponent));
